import type { CompanyOutcome } from '@nestjs-boilerplate/types';
import { Link } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { hasPermission } from '@/lib/permissions';
import { userAtom } from '@/stores/auth.store';
import { useAdminUser, useCompanyOutcome } from '../api/users';
import { CorrectClaimDialog } from './correct-claim-dialog';
import { ReassignCompanyDialog } from './reassign-company-dialog';
import { formatBalance, formatDate, formatDateTime, statusOf } from './user-display';
import { UserStatusActions } from './user-status-actions';

/**
 * One label/value pair.
 *
 * Owns the "nothing recorded" case so no card has to repeat the check — a blank
 * cell and a missing field look identical otherwise.
 */
function Field({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      {/* The dash stays legible but drops out of the scan — a card of absent
          fields should not read as densely as a card of real ones. */}
      <dd
        className={
          empty ? 'text-sm text-muted-foreground/60' : 'text-sm font-medium text-foreground'
        }
      >
        {empty ? '—' : value}
      </dd>
    </div>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  // Two columns, not three: each card is half the page once they pair up, and
  // a third column there squeezes the values back into their own labels.
  return <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2">{children}</dl>;
}

/**
 * What approving this applicant will do with the company they claimed.
 *
 * Deliberately worded to match `CompanyNotice` in user-status-actions.tsx. The
 * two answer the same question from the same endpoint, one on the card and one
 * in the confirmation dialog, and an administrator who reads a different story
 * in each has no way to tell which is current.
 */
function ClaimOutcomeNotice({
  outcome,
  isLoading,
}: {
  outcome: CompanyOutcome | undefined;
  isLoading: boolean;
}) {
  // Held at the same height as the resolved notice so the card does not jump
  // once the answer lands.
  if (isLoading || !outcome) {
    return (
      <p className="mb-4 rounded-md bg-muted p-3 text-sm text-muted-foreground">
        Entered by the applicant at setup. Checking whether it matches an existing company…
      </p>
    );
  }

  if (outcome.kind === 'ambiguous') {
    return (
      <p className="mb-4 rounded-md bg-destructive/10 p-3 text-destructive text-sm">
        More than one company holds the number <strong>{outcome.companyNumber}</strong>. Resolve the
        duplicate before approving this applicant.
      </p>
    );
  }

  return (
    <p className="mb-4 rounded-md bg-muted p-3 text-sm">
      Entered by the applicant at setup.{' '}
      {outcome.kind === 'attach' ? (
        <>
          These details match{' '}
          <strong>
            {outcome.companyName}
            {outcome.companyNumber ? ` (${outcome.companyNumber})` : ''}
          </strong>
          . Approving will add this user to that company, and they will share its balance.
        </>
      ) : (
        <>
          No existing company matches these details. Approving will create{' '}
          <strong>{outcome.companyName}</strong> as a new company.
        </>
      )}
    </p>
  );
}

/**
 * The full due-diligence record for one user.
 *
 * Read-only by design: approve/reject and disable are separate tickets, and they
 * attach to the header block below.
 */
export function AdminUserDetailPage({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useAdminUser(userId);

  // Resolved here as well as in the approve dialog, because the claim card
  // used to assert "no company record exists yet" without ever asking. That is
  // wrong whenever the number matches an existing firm — which is the case an
  // administrator most needs to see, since approving then grants this person
  // spending rights over that firm's pooled balance. Only fetched while a
  // claim is actually pending; an assigned company has nothing to resolve.
  const claimPending = Boolean(data?.claimedCompany);
  const companyOutcome = useCompanyOutcome(userId, claimPending);

  // The move endpoint is guarded by `admin:users`, but the only list of firms
  // to move somebody *to* is the creditable one, which needs `admin:billing`.
  // The two permissions are deliberately independent and separately grantable,
  // so an `admin:users`-only administrator would otherwise get the button, open
  // the dialog, and find nothing selectable in it.
  //
  // Hiding the affordance is the honest answer while one list serves both: a
  // dedicated `admin:users` company lookup would be the way to widen it, and
  // that is a bigger change than this repair path warrants. The API guards
  // remain the real boundary either way.
  const user = useAtomValue(userAtom);
  const canPickCompany = hasPermission(user, 'admin:billing');

  if (isLoading) {
    return (
      <div data-testid="admin-user-detail-loading" className="space-y-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    // A stale link is an ordinary thing to follow. It should read as a missing
    // record, not as a crash.
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <p className="text-sm text-muted-foreground">User not found</p>
          <Link to="/admin/users" className="text-sm underline">
            Back to users
          </Link>
        </CardContent>
      </Card>
    );
  }

  const status = statusOf(data.account);
  const name = [data.applicant.forename, data.applicant.surname].filter(Boolean).join(' ');
  // The firm they belong to, or the one they claimed while awaiting approval.
  // Never both: the claim is deleted when the company is assigned.
  const company = data.company ?? data.claimedCompany;

  // Full width, two cards abreast once there is room for them. Capping the page
  // instead kept the label-value pairs close but left a single column of cards
  // with dead space either side; pairing them uses the width without letting
  // any one card grow wide enough to strand a label from its value.
  return (
    <div className="space-y-6">
      <div className="space-y-4 border-b pb-6">
        <Link
          to="/admin/users"
          className="inline-block text-sm text-muted-foreground hover:text-foreground"
        >
          &larr; Back to users
        </Link>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="text-2xl font-semibold tracking-tight">{name || data.email}</h2>
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${status.className}`}>
            {status.label}
          </span>
          <div className="ml-auto">
            <UserStatusActions userId={userId} accountStatus={data.account.accountStatus} />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{data.email}</p>
      </div>

      {/* items-start so a short card does not stretch to match a tall
          neighbour, which would leave it padded with empty space. */}
      <div className="grid items-start gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Applicant</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldGrid>
              <Field label="Title" value={data.applicant.title} />
              <Field label="Forename" value={data.applicant.forename} />
              <Field label="Middle name" value={data.applicant.middleName} />
              <Field label="Surname" value={data.applicant.surname} />
              <Field label="Job title" value={data.applicant.jobTitle} />
            </FieldGrid>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Personal address</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldGrid>
              <Field label="Address line 1" value={data.address.addressLine1} />
              <Field label="Address line 2" value={data.address.addressLine2} />
              <Field label="Town or city" value={data.address.townCity} />
              <Field label="County" value={data.address.county} />
              <Field label="Postcode" value={data.address.postcode} />
              <Field label="Country" value={data.address.country} />
            </FieldGrid>
          </CardContent>
        </Card>

        {/* An applicant awaiting a decision has no company yet — only the
            details they claimed at setup. Same fields either way, so one grid
            renders both; the heading is what tells them apart. */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">
                {data.company ? 'Company' : 'Company claimed'}
              </CardTitle>
              {/* Only a claim is editable, and only while it is undecided. An
                  approved firm's record belongs to the firm — its members edit
                  it in Settings — and its number decides which firm future
                  claims match onto. */}
              {data.claimedCompany && data.account.accountStatus === 'PENDING' && (
                <CorrectClaimDialog userId={userId} claim={data.claimedCompany} />
              )}
              {/* Mutually exclusive with the claim editor above: a user has
                  either a claim awaiting a decision or a company, never both.
                  Approved only — a pending applicant's firm is still a claim,
                  and correcting that is the other button. */}
              {data.account.accountStatus === 'APPROVED' && canPickCompany && (
                <ReassignCompanyDialog
                  userId={userId}
                  userName={name || data.email}
                  currentCompanyId={data.company?.id ?? null}
                  currentCompanyName={data.company?.name ?? null}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {company ? (
              <>
                {!data.company && (
                  <ClaimOutcomeNotice
                    outcome={companyOutcome.data}
                    isLoading={companyOutcome.isLoading}
                  />
                )}
                <FieldGrid>
                  <Field label="Name" value={company.name} />
                  <Field label="Companies House number" value={company.companyNumber} />
                  <Field label="Industry" value={company.industry} />
                  <Field label="Website" value={company.website} />
                  <Field label="Address line 1" value={company.address.addressLine1} />
                  <Field label="Address line 2" value={company.address.addressLine2} />
                  <Field label="Town or city" value={company.address.townCity} />
                  <Field label="County" value={company.address.county} />
                  <Field label="Postcode" value={company.address.postcode} />
                  <Field label="Country" value={company.address.country} />
                </FieldGrid>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No company linked</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Account</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldGrid>
              {/* Only ever set on a rejected or disabled account, and cleared
                  when one is approved or re-enabled — so the label can name the
                  decision rather than saying "Status reason" at somebody who
                  can see the status. Spans the grid: 500 characters of free
                  text in a half-width cell wraps into a column. */}
              {data.account.statusReason && (
                <div className="sm:col-span-2">
                  <Field
                    label={
                      data.account.accountStatus === 'REJECTED'
                        ? 'Reason for rejection'
                        : 'Reason for disabling'
                    }
                    value={data.account.statusReason}
                  />
                </div>
              )}
              <Field label="Role" value={data.account.roleName} />
              <Field label="Email verified" value={data.account.emailVerified ? 'Yes' : 'No'} />
              <Field label="Verified at" value={formatDateTime(data.account.emailVerifiedAt)} />
              <Field
                label="Setup complete"
                value={data.account.accountSetupComplete ? 'Yes' : 'No'}
              />
              <Field label="DD alert frequency" value={data.account.dueDiligenceAlertFrequency} />
              <Field label="Failed logins" value={data.account.failedLoginAttempts} />
              <Field label="Locked until" value={formatDateTime(data.account.lockedUntil)} />
              <Field label="Deleted at" value={formatDateTime(data.account.deletedAt)} />
              <Field
                label="Deletion requested"
                value={formatDateTime(data.account.deleteRequestedAt)}
              />
              <Field label="Joined" value={formatDate(data.account.createdAt)} />
              <Field label="Last updated" value={formatDateTime(data.account.updatedAt)} />
            </FieldGrid>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldGrid>
              <Field label="Assessments" value={data.summary.assessmentCount} />
              <Field label="Balance" value={formatBalance(data.summary.balance)} />
              <Field
                label="Last login"
                value={formatDateTime(data.summary.lastLoginAt) ?? 'Never'}
              />
            </FieldGrid>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
